import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import api from '../api/client';
import logo from '../logo.png';

const COLS = ['B', 'I', 'N', 'G', 'O'];

// Single ocean theme — only marked/win state adds color
const CELL_BG       = '#0f2137';   // unmarkiert, dunkel
const CELL_BG_HOV   = '#162d47';
const CELL_MARKED   = '#1e4976';   // markiert, kräftiges ocean
const CELL_WIN      = '#d97706';   // gewonnen, sand/amber
const HEADER_BG     = '#0a1a2e';
const GRID_BG       = '#071220';
const CARD_BG       = '#071220';

function checkBingo(marked, numbers) {
  const grid = numbers.map((n) => n === 0 || marked.includes(n));
  const wins = [];
  for (let r = 0; r < 5; r++) {
    if ([0,1,2,3,4].every((c) => grid[r * 5 + c])) wins.push([0,1,2,3,4].map((c) => r * 5 + c));
  }
  for (let c = 0; c < 5; c++) {
    if ([0,1,2,3,4].every((r) => grid[r * 5 + c])) wins.push([0,1,2,3,4].map((r) => r * 5 + c));
  }
  if ([0,6,12,18,24].every((i) => grid[i])) wins.push([0,6,12,18,24]);
  if ([4,8,12,16,20].every((i) => grid[i])) wins.push([4,8,12,16,20]);
  return wins;
}

export default function BingoCard({ token: tokenProp }) {
  const params = useParams();
  const token = tokenProp || params.token;

  const [card, setCard]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [clearing, setClearing] = useState(false);

  const load = useCallback(() => {
    api.get(`/api/bingo/card/${token}`)
      .then((r) => setCard(r.data))
      .catch(() => setError('Karte nicht gefunden.'))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (num) => {
    if (num === 0) return;
    const prev = card.marked;
    const next = prev.includes(num) ? prev.filter((n) => n !== num) : [...prev, num];
    setCard({ ...card, marked: next });
    try {
      const r = await api.post(`/api/bingo/card/${token}/mark`, { number: num });
      setCard((c) => ({ ...c, marked: r.data.marked }));
    } catch {
      setCard((c) => ({ ...c, marked: prev }));
    }
  };

  const clear = async () => {
    if (!window.confirm('Alle Markierungen löschen?')) return;
    setClearing(true);
    try {
      await api.post(`/api/bingo/card/${token}/clear`);
      setCard((c) => ({ ...c, marked: [] }));
    } finally { setClearing(false); }
  };

  if (loading) return (
    <div className="flex justify-center py-16">
      <div className="animate-spin rounded-full h-8 w-8 border-2 border-ocean-400 border-t-transparent" />
    </div>
  );
  if (error) return <div className="card text-center py-12 text-slate-400">{error}</div>;
  if (!card) return null;

  const { numbers, marked } = card;
  const winLines   = checkBingo(marked, numbers);
  const winIndices = new Set(winLines.flat());
  const hasBingo   = winLines.length > 0;

  return (
    <div className="max-w-sm mx-auto">
      {hasBingo && (
        <div className="mb-4 rounded-xl bg-amber-500 text-white text-center py-3 px-4 font-black text-2xl tracking-widest shadow-lg animate-pulse">
          🎉 BINGO! 🎉
        </div>
      )}

      <div
        className="rounded-2xl overflow-hidden shadow-2xl"
        style={{ background: CARD_BG, border: '1px solid #1a3050' }}
      >
        {/* Logo + Titel */}
        <div
          className="flex items-center justify-between px-5 py-4"
          style={{ background: HEADER_BG, borderBottom: '1px solid #1a3050' }}
        >
          <img src={logo} alt="BeachLAN" style={{ height: 36, objectFit: 'contain' }} />
          <div className="text-right">
            <p className="text-white font-black text-lg tracking-widest uppercase leading-none">BeachLAN-BINGO</p>
            {card.name && <p className="text-ocean-400 text-xs mt-0.5">{card.name}</p>}
          </div>
        </div>

        {/* Spalten-Header B I N G O */}
        <div className="grid grid-cols-5" style={{ background: '#0d2035', borderBottom: '1px solid #1a3050' }}>
          {COLS.map((col) => (
            <div key={col} className="flex items-center justify-center py-2.5 font-black text-sand-300 text-xl tracking-widest">
              {col}
            </div>
          ))}
        </div>

        {/* Zellen */}
        <div className="grid grid-cols-5 gap-px" style={{ background: '#1a3050', padding: 1 }}>
          {numbers.map((num, idx) => {
            const isFree   = num === 0;
            const isMarked = isFree || marked.includes(num);
            const isWin    = winIndices.has(idx);

            return (
              <button
                key={idx}
                onClick={() => toggle(num)}
                disabled={isFree}
                style={{
                  background: isWin ? CELL_WIN : isMarked ? CELL_MARKED : CELL_BG,
                  border: 'none',
                  cursor: isFree ? 'default' : 'pointer',
                  transition: 'background 0.15s, transform 0.1s',
                  transform: isMarked && !isFree ? 'scale(0.93)' : 'scale(1)',
                  aspectRatio: '1',
                }}
                className="flex items-center justify-center relative"
              >
                {isFree ? (
                  <span style={{ color: '#60a5fa', fontSize: '0.6rem', fontWeight: 900, letterSpacing: '0.05em' }}>
                    FREE
                  </span>
                ) : (
                  <>
                    <span style={{
                      color: isWin ? '#fff' : isMarked ? '#e0f0ff' : '#93c5fd',
                      fontSize: 'clamp(0.8rem, 3.5vw, 1.15rem)',
                      fontWeight: 900,
                      fontVariantNumeric: 'tabular-nums',
                      lineHeight: 1,
                    }}>
                      {num}
                    </span>
                    {isMarked && (
                      <span className="absolute inset-0 flex items-center justify-center" style={{ pointerEvents: 'none' }}>
                        <span style={{
                          width: '65%', height: '65%',
                          borderRadius: '50%',
                          border: '1.5px solid rgba(96,165,250,0.35)',
                          background: 'rgba(96,165,250,0.08)',
                        }} />
                      </span>
                    )}
                  </>
                )}
              </button>
            );
          })}
        </div>

        {/* Footer */}
        <div
          className="px-5 py-3 flex justify-between items-center"
          style={{ background: HEADER_BG, borderTop: '1px solid #1a3050' }}
        >
          <span style={{ color: '#4a7fa5', fontSize: '0.7rem', fontWeight: 600 }}>
            {marked.filter((n) => n !== 0).length} / 24 markiert
          </span>
          <button
            onClick={clear}
            disabled={clearing || marked.length === 0}
            style={{
              color: clearing || marked.length === 0 ? '#2a4a6a' : '#60a5fa',
              fontSize: '0.7rem',
              fontWeight: 700,
              background: 'none',
              border: 'none',
              cursor: clearing || marked.length === 0 ? 'default' : 'pointer',
              transition: 'color 0.15s',
            }}
          >
            Zurücksetzen
          </button>
        </div>
      </div>

      <p className="text-center text-xs text-slate-400 mt-3">
        Zahl antippen zum Markieren · Bingo bei Reihe, Spalte oder Diagonale
      </p>
    </div>
  );
}
